import { CMPAuditDto } from "../../../../common/service/server/dto/TrueScanResultsDto";

export const enum AudiitItemLevel {
  PASSED = "passed",
  WARNING = "warning",
  ERROR = "error",
}

export interface AuditItem {
  name: string;
  level: AudiitItemLevel;
  helpText?: string;
}

const POLARIS_JS_SCRIPT_NAME = "polaris.js <script> tag";

/**
 * We consolidate some of the CMP Audit checks to the same audit item (like "use the right polaris.js script"), with the
 * help text varying slightly depending on the specific check that failed.
 */
export const generateAuditItems = (cmpAudit: CMPAuditDto): AuditItem[] => {
  if (!cmpAudit) {
    return [];
  }

  let auditItems: AuditItem[] = [];

  // The order here affects the display order or the checks in the UI, barring any errors (which bubble to the top).
  auditItems.push(checkPolarisPresent(cmpAudit));
  auditItems.push(checkPolarisVersion(cmpAudit));
  auditItems.push(checkPrivacyCenterId(cmpAudit));
  auditItems.push(checkLoadPosition(cmpAudit));
  auditItems.push(checkDeferredOrAsync(cmpAudit));
  auditItems.push(checkDuplicates(cmpAudit));
  auditItems.push(checkGTMPosition(cmpAudit));
  auditItems.push(checkShopifyApiFeatures(cmpAudit));

  // Some checks return null if not applicable, filter those out.
  auditItems = auditItems.filter((item) => item !== null);

  // We shouldn't show successful checks if there are any errors, since the verbiage can be confusing when side-by-side.
  const hasError = auditItems.some((item) => item.level === AudiitItemLevel.ERROR);
  if (hasError) {
    auditItems = auditItems.filter((item) => item.level !== AudiitItemLevel.PASSED);
  }

  return auditItems.sort((a, b) => {
    if (a.level === b.level) return 0;
    return a.level === AudiitItemLevel.PASSED ? 2 : a.level === AudiitItemLevel.WARNING ? 1 : -1; // Failed items first
  });
};

const checkDeferredOrAsync = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (cmpAudit.cmpIssueDeferredOrAsync === true) {
    return {
      name: "CMP loading asynchronously",
      level: AudiitItemLevel.ERROR,
      helpText: `The ${POLARIS_JS_SCRIPT_NAME} is loading asynchronously. It is likely marked with the 'defer' or 'async' attribute. Remove these attributes to ensure it communicates consent settings to GTM & Shopify correctly. Note: these attributes are common causes but the CMP could also be loading asynchronously if another script is managing or injecting ${POLARIS_JS_SCRIPT_NAME}, for example.`,
    };
  }
  if (cmpAudit.cmpIssueDeferredOrAsync === false) {
    return {
      name: "CMP is loading correctly without 'defer' or 'async' attributes",
      level: AudiitItemLevel.PASSED,
    };
  }
  return null;
};

const checkDuplicates = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (cmpAudit.cmpIssueDuplicatesPresent === true) {
    return {
      name: "Multiple scripts detected",
      level: AudiitItemLevel.ERROR,
      helpText: `Multiple copies of the ${POLARIS_JS_SCRIPT_NAME} are being loaded on the page. Ensure only one ${POLARIS_JS_SCRIPT_NAME} is used to avoid conflicts.`,
    };
  }

  return null;
};

const checkGTMPosition = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (!cmpAudit.isUsingGtm) {
    return null;
  }

  if (cmpAudit.cmpIssueLocatedAfterGtm === true) {
    return {
      name: "GTM script loaded before CMP",
      level: AudiitItemLevel.ERROR,
      helpText: `The ${POLARIS_JS_SCRIPT_NAME} should be loaded before the Google Tag Manager script to ensure proper functionality.`,
    };
  }
  if (cmpAudit.cmpIssueLocatedAfterGtm === false) {
    return {
      name: "CMP script is loaded before GTM",
      level: AudiitItemLevel.PASSED,
    };
  }
  return null;
};

const checkShopifyApiFeatures = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (!cmpAudit.isUsingShopify) {
    return null;
  }

  if (cmpAudit.cmpIssueShopifyApiMissingLoadFeatures === true) {
    return {
      name: "Shopify API features not detected",
      level: AudiitItemLevel.WARNING,
      helpText:
        "We couldn't detect the required Shopify API features ('loadFeatures' and 'customerPrivacy') on your storefront. These are necessary if you are using a banner to control Shopify consent. Please reach out to your TrueVault Customer Support Representative for assistance.",
    };
  }
  if (cmpAudit.cmpIssueShopifyApiMissingLoadFeatures === false) {
    return {
      name: "Required Shopify API features detected",
      level: AudiitItemLevel.PASSED,
    };
  }
  return null;
};

const checkLoadPosition = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (
    cmpAudit.cmpTagIndexInHead === -1 ||
    cmpAudit.cmpTagIndexInHead === undefined ||
    cmpAudit.cmpTagIndexInHead === null
  ) {
    return null;
  }

  if (cmpAudit.cmpTagIndexInHead > 0) {
    return {
      name: `At least one script is loading before the ${POLARIS_JS_SCRIPT_NAME}`,
      level: AudiitItemLevel.WARNING,
      helpText: `The ${POLARIS_JS_SCRIPT_NAME} is loading after ${
        cmpAudit.cmpTagIndexInHead + 1
      } other scripts. It should be loaded as the first script on the page. This may not always be possible due to other scripts dynamically adding their code in the page. Review your page setup to ensure the CMP script is loading as early in the <head> tag as possible. If you've confirmed that the CMP script is the first functional script when you view-source, you can ignore this warning.`,
    };
  }

  return {
    name: "CMP script is the first functional script",
    level: AudiitItemLevel.PASSED,
  };
};

const checkPrivacyCenterId = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (cmpAudit.cmpIssueWrongOrg === true) {
    return {
      name: "CMP is not using the correct organization ID",
      level: AudiitItemLevel.ERROR,
      helpText: `The ${POLARIS_JS_SCRIPT_NAME} is not using the correct organization ID. Ensure you are using the unique custom link provided in your organization's Developer Instructions. Using another org's ${POLARIS_JS_SCRIPT_NAME} is not supported.`,
    };
  }
  if (cmpAudit.cmpIssueWrongOrg === false) {
    return {
      name: "CMP is using the correct organization ID",
      level: AudiitItemLevel.PASSED,
    };
  }
  return null;
};

const checkPolarisPresent = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (cmpAudit.cmpIssueWrongOrg === true) {
    // already showing a similar error
    return null;
  }

  if (
    !cmpAudit.cmpVersion &&
    (cmpAudit.cmpTagIndexInHead === -1 || cmpAudit.cmpTagIndexInHead === null)
  ) {
    return {
      name: "CMP not detected",
      level: AudiitItemLevel.ERROR,
      helpText: `The ${POLARIS_JS_SCRIPT_NAME} was not detected on your site. Ensure you are loading it from your organization's unique link found in Developer Instructions.`,
    };
  }

  return {
    name: "CMP detected on your site",
    level: AudiitItemLevel.PASSED,
  };
};

const checkPolarisVersion = (cmpAudit: CMPAuditDto): AuditItem | null => {
  if (cmpAudit.cmpIssueWrongOrg === true) {
    // already showing a similar error
    return null;
  }

  if (
    !cmpAudit.cmpVersion &&
    cmpAudit.cmpTagIndexInHead !== null &&
    cmpAudit.cmpTagIndexInHead >= 0
  ) {
    // No version detected, but CMP script is present - means we're using the static script.
    return {
      name: "CMP version is outdated",
      level: AudiitItemLevel.ERROR,
      helpText: `The ${POLARIS_JS_SCRIPT_NAME} on your site is not up to date. Ensure you are using the organization specific link found in Developer Instructions.`,
    };
  }

  return null;
};
